"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.listThreadDirs = listThreadDirs;
exports.findMarkdownFiles = findMarkdownFiles;
exports.findDocumentById = findDocumentById;
exports.findThreadPath = findThreadPath;
exports.resolveWeaveSlugForPlan = resolveWeaveSlugForPlan;
exports.gatherAllDocumentIds = gatherAllDocumentIds;
const fs = __importStar(require("fs-extra"));
const path = __importStar(require("path"));
const yaml = __importStar(require("yaml"));
// `reports` holds standalone report-artifact snapshots (weave-scoped:
// loom/{weave}/reports/) — deliberately NOT a thread and NOT part of the doc graph
// (see loom-ai-analysis storage decision A), so it is reserved here too.
const RESERVED_SUBDIR_NAMES = new Set(['plans', 'done', 'chats', 'ctx', 'refs', 'reports', '.archive']);
/**
 * Returns thread subdirectory names within a weave folder.
 * A subdir qualifies as a thread if it is not reserved and contains an idea/design doc
 * or a non-empty plans/ subdir.
 */
async function listThreadDirs(weavePath) {
    if (!await fs.pathExists(weavePath))
        return [];
    const entries = await fs.readdir(weavePath, { withFileTypes: true });
    return entries
        .filter(e => e.isDirectory() && !RESERVED_SUBDIR_NAMES.has(e.name))
        .map(e => e.name);
}
/**
 * Recursively finds all Markdown files in a directory, excluding _archive.
 */
async function findMarkdownFiles(dir) {
    const result = [];
    const entries = await fs.readdir(dir, { withFileTypes: true });
    for (const entry of entries) {
        const fullPath = path.join(dir, entry.name);
        // Skip `reports/` — report artifacts are not graph nodes (storage decision A),
        // so they never enter the link index, diagnostics, or id-uniqueness scans.
        if (entry.isDirectory() && entry.name !== '.archive' && entry.name !== 'reports') {
            result.push(...await findMarkdownFiles(fullPath));
        }
        else if (entry.isFile() && entry.name.endsWith('.md')) {
            result.push(fullPath);
        }
    }
    return result;
}
async function readFrontmatterId(filePath) {
    try {
        const content = await fs.readFile(filePath, 'utf-8');
        const match = content.match(/^---\r?\n([\s\S]*?)\r?\n---/);
        if (!match)
            return null;
        const fm = yaml.parse(match[1]);
        return typeof fm?.id === 'string' ? fm.id : null;
    }
    catch {
        return null;
    }
}
/**
 * Recursively searches for a document by its frontmatter `id` field.
 * Returns the absolute file path if found, otherwise null.
 */
async function findDocumentById(loomRoot, id) {
    const files = await findMarkdownFiles(loomRoot);
    for (const file of files) {
        if (await readFrontmatterId(file) === id)
            return file;
    }
    return null;
}
/**
 * Locates the thread directory for a given thread ID.
 * Returns the absolute path if found, otherwise null.
 */
async function findThreadPath(loomRoot, weaveSlug) {
    const threadsDir = path.join(loomRoot, 'loom');
    const threadPath = path.join(threadsDir, weaveSlug);
    if (await fs.pathExists(threadPath)) {
        return threadPath;
    }
    return null;
}
/**
 * Resolves the weave slug for a given plan by locating the plan file on disk.
 * Searches under {loomRoot}/loom/ and returns the first path component (the weave dir).
 * A plan is addressed by its ULID; the weave slug is recovered from the resolved
 * *path*, never by parsing the id.
 */
async function resolveWeaveSlugForPlan(loomRoot, planUlid) {
    const loomDir = path.join(loomRoot, 'loom');
    const planPath = await findDocumentById(loomDir, planUlid);
    if (!planPath) {
        throw new Error(`Plan '${planUlid}' not found under ${loomDir}`);
    }
    const rel = path.relative(loomDir, planPath);
    const weaveSlug = rel.split(path.sep)[0];
    if (!weaveSlug) {
        throw new Error(`Could not derive weave slug from plan path: ${planPath}`);
    }
    return weaveSlug;
}
/**
 * Gathers all document IDs (from frontmatter) from the entire loom.
 * Useful for uniqueness checks when generating new IDs.
 */
async function gatherAllDocumentIds(loomRoot) {
    const ids = new Set();
    const files = await findMarkdownFiles(loomRoot);
    for (const file of files) {
        const id = await readFrontmatterId(file);
        if (id)
            ids.add(id);
    }
    return ids;
}
